'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Divider } from '@sola/ark-core/atoms/divider/divider.tsx';
import { Form, useFormMethods } from '@sola/ark-core/molecules/form/form.tsx';
import { ModalProps } from '@sola/ark-core/molecules/modal/modal.tsx';
import { Sheet, SheetActions, SheetBody, SheetBodyHeader, SheetClose, SheetContent, SheetFooter, SheetHeader } from '@sola/ark-core/molecules/sheet/sheet.tsx';
import { useCart } from '@sola/cart-sdk/store.tsx';
import { CommitmentCartPaymentItem, useCommitmentPaymentsAdd } from '@sola/my-sdk/commitments/hooks.ts';
import { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { isItemPayment } from '@sola/shared-utils/cart/cart-item-type-guards.ts';
import { useMakeAPaymentCommitments } from '@sola/supporter-sdk/hooks/commitments.ts';
import { useLocalAlerts } from '../../molecules/local-alerts/local-alerts-provider.tsx';
import { LocalAlerts } from '../../molecules/local-alerts/local-alerts.tsx';
import { MakeAPaymentSection } from '../../molecules/make-a-payment-section/make-a-payment-section.tsx';
import { createCommitmentPaymentAddErrorAlert } from '../../utils/alerts.tsx';
const getDefaultFormValues = (missedPayments: GraphQLCommitment[], amountDue: GraphQLCommitment[], payAheadCommitments: GraphQLCommitment[], commitmentCartItemsMap: Map<string, CommitmentCartPaymentItem>) => {
  // Check if there are any cart values for the current commitments, if there are we will use those as the default form values, if not we will use the amounts from the commitments.
  const hasCartValues = commitmentCartItemsMap.size > 0;
  return [...missedPayments, ...amountDue, ...payAheadCommitments].reduce((acc, commitment) => {
    const commitmentId = commitment.internalId;
    if (!commitmentId) {
      return acc;
    }
    const cartValue = commitmentCartItemsMap.get(commitmentId)?.unitPrice;
    acc[commitmentId] = cartValue ?? (hasCartValues ? null : (commitment.dueThisMonthAmount || 0) + (commitment.missedPaymentAmount || 0) || null);
    return acc;
  }, {} as Record<string, number | null>);
};
const MakeAPaymentSheetTotalAmount: React.FC<{
  totalAmount: number;
}> = t0 => {
  const $ = _c(16);
  const {
    totalAmount
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["my", "core"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  let t2;
  if ($[1] !== t) {
    t2 = t("my:makeAPayment.total");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <span className="label-potent label-lg label-neutral-mild inline-block">{t2}</span>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t || $[6] !== totalAmount) {
    t4 = t("core:numbers.currencyCustom", {
      amount: totalAmount
    });
    $[5] = t;
    $[6] = totalAmount;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <span className="label-xl label-potent label-neutral-amplified">{t4}</span>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t3 || $[11] !== t5) {
    t6 = <div className="flex flex-col gap-snug-lg">{t3}{t5}</div>;
    $[10] = t3;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Divider purpose="neutral" />;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t6) {
    t8 = <SheetBodyHeader isSticky={true} className="border flex flex-col gap-comfy-xl">{t6}{t7}</SheetBodyHeader>;
    $[14] = t6;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
};
export interface MakeAPaymentSheetProps {
  open?: ModalProps['open'];
  onOpenChange?: ModalProps['onOpenChange'];
}
export const MakeAPaymentSheet: React.FC<MakeAPaymentSheetProps> = t0 => {
  const $ = _c(102);
  const {
    open,
    onOpenChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["my", "core"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  const {
    cart
  } = useCart("cart");
  const {
    amountDue,
    missedPaymentCommitments,
    amountDueCommitments,
    payAheadCommitments
  } = useMakeAPaymentCommitments();
  let t2;
  if ($[1] !== amountDueCommitments || $[2] !== missedPaymentCommitments || $[3] !== payAheadCommitments) {
    t2 = new Map([...missedPaymentCommitments, ...amountDueCommitments, ...payAheadCommitments].flatMap(_temp));
    $[1] = amountDueCommitments;
    $[2] = missedPaymentCommitments;
    $[3] = payAheadCommitments;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const currentMakeAPaymentCommitmentIds = t2;
  let commitmentCartItemsMap;
  let t3;
  let totalAmountInCart;
  if ($[5] !== amountDueCommitments || $[6] !== cart?.items || $[7] !== currentMakeAPaymentCommitmentIds || $[8] !== missedPaymentCommitments || $[9] !== payAheadCommitments) {
    let t4;
    if ($[13] !== currentMakeAPaymentCommitmentIds) {
      t4 = item => currentMakeAPaymentCommitmentIds.has(item.payment.commitmentId);
      $[13] = currentMakeAPaymentCommitmentIds;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    commitmentCartItemsMap = new Map((cart?.items ?? []).filter(isItemPayment).filter(t4).map(_temp2));
    totalAmountInCart = commitmentCartItemsMap.size ? Array.from(commitmentCartItemsMap.values()).reduce(_temp3, 0) : 0;
    t3 = getDefaultFormValues(missedPaymentCommitments, amountDueCommitments, payAheadCommitments, commitmentCartItemsMap);
    $[5] = amountDueCommitments;
    $[6] = cart?.items;
    $[7] = currentMakeAPaymentCommitmentIds;
    $[8] = missedPaymentCommitments;
    $[9] = payAheadCommitments;
    $[10] = commitmentCartItemsMap;
    $[11] = t3;
    $[12] = totalAmountInCart;
  } else {
    commitmentCartItemsMap = $[10];
    t3 = $[11];
    totalAmountInCart = $[12];
  }
  const defaultFormValues = t3;
  const {
    addAlerts,
    clearAlerts
  } = useLocalAlerts();
  const formMethods = useFormMethods();
  const {
    mutateAsync
  } = useCommitmentPaymentsAdd();
  const [isPending, setIsPending] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  let t4;
  if ($[15] !== amountDue || $[16] !== clearAlerts || $[17] !== totalAmountInCart) {
    t4 = () => {
      clearAlerts();
      setTotalAmount(totalAmountInCart > 0 ? totalAmountInCart : amountDue ?? 0);
    };
    $[15] = amountDue;
    $[16] = clearAlerts;
    $[17] = totalAmountInCart;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== amountDue || $[20] !== open || $[21] !== totalAmountInCart) {
    t5 = [amountDue, totalAmountInCart, open];
    $[19] = amountDue;
    $[20] = open;
    $[21] = totalAmountInCart;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  useEffect(t4, t5);
  const calculateTotalFromFormValues = _temp5;
  let t6;
  if ($[23] !== addAlerts || $[24] !== clearAlerts || $[25] !== mutateAsync) {
    t6 = async data => {
      setIsPending(true);
      try {
        clearAlerts();
        await mutateAsync({
          payments: data
        });
      } catch {
        setIsPending(false);
        addAlerts(createCommitmentPaymentAddErrorAlert());
      }
    };
    $[23] = addAlerts;
    $[24] = clearAlerts;
    $[25] = mutateAsync;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  const handleSubmit = t6;
  const canSubmit = totalAmount > 0 || commitmentCartItemsMap.size > 0;
  let t7;
  if ($[27] !== t) {
    t7 = t("makeAPayment.title");
    $[27] = t;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  let t8;
  if ($[29] !== t) {
    t8 = t("makeAPayment.title");
    $[29] = t;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== t8) {
    t9 = <SheetHeader>{t8}</SheetHeader>;
    $[31] = t8;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] !== totalAmount) {
    t10 = <MakeAPaymentSheetTotalAmount totalAmount={totalAmount} />;
    $[33] = totalAmount;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== formMethods.current) {
    t11 = () => {
      setTotalAmount(calculateTotalFromFormValues(formMethods.current?.getValues() || {}));
    };
    $[35] = formMethods.current;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== defaultFormValues) {
    t12 = {
      mode: "onTouched",
      defaultValues: defaultFormValues
    };
    $[37] = defaultFormValues;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <LocalAlerts scrollPosition="end" />;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== t) {
    t14 = t("makeAPayment.missingPayment.title");
    $[40] = t;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== t) {
    t15 = t("makeAPayment.missingPayment.subtitle");
    $[42] = t;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== missedPaymentCommitments || $[45] !== t14 || $[46] !== t15) {
    t16 = <MakeAPaymentSection title={t14} description={t15} commitments={missedPaymentCommitments} />;
    $[44] = missedPaymentCommitments;
    $[45] = t14;
    $[46] = t15;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] !== t) {
    t17 = t("makeAPayment.paymentDue.title");
    $[48] = t;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== t) {
    t18 = t("makeAPayment.paymentDue.subtitle");
    $[50] = t;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== amountDueCommitments || $[53] !== t17 || $[54] !== t18) {
    t19 = <MakeAPaymentSection title={t17} description={t18} commitments={amountDueCommitments} />;
    $[52] = amountDueCommitments;
    $[53] = t17;
    $[54] = t18;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  let t20;
  if ($[56] !== t) {
    t20 = t("makeAPayment.payAhead.alertMessage");
    $[56] = t;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  let t21;
  if ($[58] !== t) {
    t21 = t("makeAPayment.payAhead.title");
    $[58] = t;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  let t22;
  if ($[60] !== t) {
    t22 = t("makeAPayment.payAhead.subtitle");
    $[60] = t;
    $[61] = t22;
  } else {
    t22 = $[61];
  }
  let t23;
  if ($[62] !== payAheadCommitments || $[63] !== t20 || $[64] !== t21 || $[65] !== t22) {
    t23 = <MakeAPaymentSection alertMessage={t20} title={t21} description={t22} commitments={payAheadCommitments} />;
    $[62] = payAheadCommitments;
    $[63] = t20;
    $[64] = t21;
    $[65] = t22;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  let t24;
  if ($[67] !== t) {
    t24 = t("core:actions.cancel");
    $[67] = t;
    $[68] = t24;
  } else {
    t24 = $[68];
  }
  let t25;
  if ($[69] !== t24) {
    t25 = <SheetClose asChild={true}><ButtonStyle size="lg" purpose="neutral" variant="secondary" fullWidth={true}><button type="button">{t24}</button></ButtonStyle></SheetClose>;
    $[69] = t24;
    $[70] = t25;
  } else {
    t25 = $[70];
  }
  const t26 = !canSubmit || isPending;
  let t27;
  if ($[71] !== t) {
    t27 = t("core:actions.continue");
    $[71] = t;
    $[72] = t27;
  } else {
    t27 = $[72];
  }
  let t28;
  if ($[73] !== t27) {
    t28 = <button type="submit">{t27}</button>;
    $[73] = t27;
    $[74] = t28;
  } else {
    t28 = $[74];
  }
  let t29;
  if ($[75] !== isPending || $[76] !== t26 || $[77] !== t28) {
    t29 = <ButtonStyle fullWidth={true} isBusy={isPending} disabled={t26}>{t28}</ButtonStyle>;
    $[75] = isPending;
    $[76] = t26;
    $[77] = t28;
    $[78] = t29;
  } else {
    t29 = $[78];
  }
  let t30;
  if ($[79] !== t25 || $[80] !== t29) {
    t30 = <SheetFooter><SheetActions>{t25}{t29}</SheetActions></SheetFooter>;
    $[79] = t25;
    $[80] = t29;
    $[81] = t30;
  } else {
    t30 = $[81];
  }
  let t31;
  if ($[82] !== formMethods || $[83] !== handleSubmit || $[84] !== t11 || $[85] !== t12 || $[86] !== t16 || $[87] !== t19 || $[88] !== t23 || $[89] !== t30) {
    t31 = <Form className="mt-comfy-xl flex flex-col gap-comfy-xl" onChange={t11} onSubmit={handleSubmit} formMethods={formMethods} options={t12}>{t13}{t16}{t19}{t23}{t30}</Form>;
    $[82] = formMethods;
    $[83] = handleSubmit;
    $[84] = t11;
    $[85] = t12;
    $[86] = t16;
    $[87] = t19;
    $[88] = t23;
    $[89] = t30;
    $[90] = t31;
  } else {
    t31 = $[90];
  }
  let t32;
  if ($[91] !== t10 || $[92] !== t31) {
    t32 = <SheetBody>{t10}{t31}</SheetBody>;
    $[91] = t10;
    $[92] = t31;
    $[93] = t32;
  } else {
    t32 = $[93];
  }
  let t33;
  if ($[94] !== t32 || $[95] !== t7 || $[96] !== t9) {
    t33 = <SheetContent title={t7}>{t9}{t32}</SheetContent>;
    $[94] = t32;
    $[95] = t7;
    $[96] = t9;
    $[97] = t33;
  } else {
    t33 = $[97];
  }
  let t34;
  if ($[98] !== onOpenChange || $[99] !== open || $[100] !== t33) {
    t34 = <Sheet open={open} onOpenChange={onOpenChange}>{t33}</Sheet>;
    $[98] = onOpenChange;
    $[99] = open;
    $[100] = t33;
    $[101] = t34;
  } else {
    t34 = $[101];
  }
  return t34;
};
function _temp(commitment) {
  return commitment.internalId ? [[commitment.internalId, commitment] as const] : [];
}
function _temp2(item_0) {
  return [item_0.payment.commitmentId, item_0] as const;
}
function _temp3(sum, item_1) {
  return sum + (item_1.unitPrice || 0);
}
function _temp4(sum_0, value) {
  return sum_0 + (typeof value === "number" && !Number.isNaN(value) ? value : 0);
}
function _temp5(values) {
  return Object.values(values).reduce(_temp4, 0);
}